/**
 * The "vault": our own IndexedDB store for imported game data.
 *
 * Why not put the .dat files straight into IDBFS?
 *  - IDBFS keeps a full in-memory MEMFS mirror and re-serialises entries on
 *    syncfs. Putting 500MB of immutable CD data behind it makes every save
 *    round-trip walk half a gigabyte.
 *  - Blobs stored here are file-backed by the browser, so a 333MB master.dat
 *    costs no JS heap until we read it.
 *
 * So: immutable imported data lives here, and IDBFS is mounted only over
 * data/SAVEGAME, where the engine actually writes.
 */

const LEGACY_DB_NAME = 'f2ce-shell';
const DB_VERSION = 2;
let activeName: string = LEGACY_DB_NAME;
export const STORE_FILES = 'files';
export const STORE_META = 'meta';
/**
 * Saves lifted out of a restored backup, held before they are promoted.
 *
 * They land here rather than in a slot because the design's collision rule is
 * absolute: a restore never overwrites a live slot silently. This is the
 * "archive" the recovery copy promises — the player promotes from it, and only
 * then does anything reach SAVEGAME.
 */
export const STORE_RESTORED = 'restored';

export interface VaultEntry {
  path: string; // relative to the game dir, forward slashes, e.g. "data/sound/music/WIND1.ACM"
  size: number;
  blob: Blob;
}

export interface InstallMeta {
  installedAt: number;
  source: 'directory' | 'zip' | 'cd' | 'dev-endpoint';
  fileCount: number;
  totalBytes: number;
  hasPatch: boolean;
  dats: string[];
  gameType?: 'fo1' | 'fo2';
}

let dbPromise: Promise<IDBDatabase> | null = null;

export function useVault(dbName: string): void {
  if (dbName === activeName && dbPromise) return;
  if (dbPromise) {
    dbPromise.then((db) => db.close()).catch(() => {});
    dbPromise = null;
  }
  activeName = dbName;
}

export function currentVaultName(): string {
  return activeName;
}

export function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(activeName, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_FILES)) db.createObjectStore(STORE_FILES, { keyPath: 'path' });
      if (!db.objectStoreNames.contains(STORE_META)) db.createObjectStore(STORE_META);
      if (!db.objectStoreNames.contains(STORE_RESTORED)) db.createObjectStore(STORE_RESTORED, { keyPath: 'id' });
    };
    req.onsuccess = () => {
      const db = req.result;
      // An open connection blocks deleteDatabase for as long as it lives, and
      // nothing ever closes this one — so "Erase everything" in a second tab,
      // or a version bump, would hang forever with no error to show. Step aside
      // instead; the next call re-opens.
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      db.onclose = () => {
        dbPromise = null;
      };
      resolve(db);
    };
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
        t.onabort = () => reject(t.error);
      })
  );
}

/** Write many entries in one transaction (much faster than one tx per file). */
export async function putFiles(entries: VaultEntry[]): Promise<void> {
  if (!entries.length) return;
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const t = db.transaction(STORE_FILES, 'readwrite');
    const s = t.objectStore(STORE_FILES);
    for (const e of entries) s.put(e);
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

export function getFile(path: string): Promise<VaultEntry | undefined> {
  return tx<VaultEntry | undefined>(STORE_FILES, 'readonly', (s) => s.get(path));
}

export async function listFiles(): Promise<{ path: string; size: number }[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const out: { path: string; size: number }[] = [];
    const t = db.transaction(STORE_FILES, 'readonly');
    const req = t.objectStore(STORE_FILES).openCursor();
    req.onsuccess = () => {
      const cur = req.result;
      if (!cur) return resolve(out);
      const v = cur.value as VaultEntry;
      out.push({ path: v.path, size: v.size });
      cur.continue();
    };
    req.onerror = () => reject(req.error);
  });
}

/** Removes vault entries by exact path. Used to evict contamination at boot. */
export async function deleteFiles(paths: string[]): Promise<void> {
  if (!paths.length) return;
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const t = db.transaction(STORE_FILES, 'readwrite');
    const s = t.objectStore(STORE_FILES);
    for (const p of paths) s.delete(p);
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

export function getMeta<T>(key: string): Promise<T | undefined> {
  return tx<T | undefined>(STORE_META, 'readonly', (s) => s.get(key));
}

export function setMeta(key: string, value: unknown): Promise<IDBValidKey> {
  return tx<IDBValidKey>(STORE_META, 'readwrite', (s) => s.put(value, key));
}

/** One save recovered from a backup and waiting to be promoted into a slot. */
export interface RestoredSave {
  id: string;
  /** Where it came from: the backup's filename and the slot it held. */
  origin: string;
  restoredAt: number;
  characterName: string;
  description: string;
  mapFile: string;
  savedAt: number | null;
  bytes: number;
  files: { path: string; blob: Blob }[];
}

export async function putRestored(saves: RestoredSave[]): Promise<void> {
  if (!saves.length) return;
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const t = db.transaction(STORE_RESTORED, 'readwrite');
    const s = t.objectStore(STORE_RESTORED);
    for (const save of saves) s.put(save);
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

export async function listRestored(): Promise<RestoredSave[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const out: RestoredSave[] = [];
    const t = db.transaction(STORE_RESTORED, 'readonly');
    const req = t.objectStore(STORE_RESTORED).openCursor();
    req.onsuccess = () => {
      const cur = req.result;
      if (!cur) return resolve(out.sort((a, b) => b.restoredAt - a.restoredAt));
      out.push(cur.value as RestoredSave);
      cur.continue();
    };
    req.onerror = () => reject(req.error);
  });
}

export function deleteRestored(id: string): Promise<undefined> {
  return tx<undefined>(STORE_RESTORED, 'readwrite', (s) => s.delete(id));
}

export async function clearVault(): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const t = db.transaction([STORE_FILES, STORE_META], 'readwrite');
    t.objectStore(STORE_FILES).clear();
    t.objectStore(STORE_META).clear();
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
  // The eviction canary lives in localStorage, outside everything we just
  // emptied, and cannot tell our own deliberate wipe from Safari's. Clearing it
  // here rather than at the call sites means a future "forget everything"
  // button cannot forget to, and cannot open the next boot with a recovery
  // banner for a deletion the player asked for. Imported lazily: durability
  // reads this module, and a static edge back would close the cycle.
  (await import('./durability')).clearDurabilityCanary();
}

/** Nukes vault(s) *and* IDBFS save databases. Used by "reset everything". */
export async function deleteEverything(vaultDbNames: string[], idbfsDbNames: string[]): Promise<void> {
  dbPromise = null;
  const kill = (name: string) =>
    new Promise<void>((resolve) => {
      const r = indexedDB.deleteDatabase(name);
      r.onsuccess = r.onerror = r.onblocked = () => resolve();
    });
  for (const n of vaultDbNames) await kill(n);
  for (const n of idbfsDbNames) await kill(n);
  // Same reason as clearVault, and more urgently: "Erase everything" reloads
  // the page straight into the boot-time canary check, which would otherwise
  // greet the player with a recovery banner for the erasure they just
  // confirmed twice.
  (await import('./durability')).clearDurabilityCanary();
}

export async function requestPersistence(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  try {
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

export async function storageEstimate(): Promise<{ usage: number; quota: number } | null> {
  if (!navigator.storage?.estimate) return null;
  const e = await navigator.storage.estimate();
  return { usage: e.usage ?? 0, quota: e.quota ?? 0 };
}
